import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FileText, 
  BookOpen, 
  Video, 
  Search, 
  Download, 
  Play,
  Phone,
  LogOut,
  Loader2,
  Filter,
  Sparkles,
  ExternalLink
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ANIMATION_CONFIG } from "@/config/animations";

interface Category {
  id: string;
  name: string;
  name_he: string;
  icon: string;
}

interface Resource {
  id: string;
  category_id: string;
  title: string;
  description: string;
  file_type: string;
  file_url: string;
  created_at: string | null;
}

const iconMap: Record<string, any> = {
  FileText: FileText,
  BookOpen: BookOpen,
  Video: Video,
};

const fileTypeLabels: Record<string, { label: string; color: string }> = {
  pdf: { label: "PDF", color: "bg-red-100 text-red-700" },
  docx: { label: "DOCX", color: "bg-blue-100 text-blue-700" },
  video: { label: "וידאו", color: "bg-purple-100 text-purple-700" },
  link: { label: "קישור", color: "bg-green-100 text-green-700" },
};

// Check if resource is new (within threshold days)
function isNewResource(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const resourceDate = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - resourceDate.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays <= ANIMATION_CONFIG.badges.recentDaysThreshold;
}

export default function Library() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<{ full_name: string } | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
      
      const { data: profileData } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .maybeSingle();
      
      if (profileData) {
        setProfile(profileData);
      }
      
      await fetchData();
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [categoriesRes, resourcesRes] = await Promise.all([
        supabase.from("resource_categories").select("*").order("sort_order"),
        supabase.from("resources").select("*").order("sort_order"),
      ]);

      if (categoriesRes.data) setCategories(categoriesRes.data);
      if (resourcesRes.data) setResources(resourcesRes.data as Resource[]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "להתראות!",
      description: "התנתקת בהצלחה",
    });
    navigate("/");
  };

  const handleResourceClick = (resource: Resource) => {
    const isVideo = resource.file_type === "video";
    const message = isVideo ? "הסרטון נפתח בחלון חדש" : "ההורדה התחילה";
    
    toast({
      title: message,
      description: resource.title,
      duration: 3000,
    });

    if (resource.file_url && resource.file_url !== "#") {
      window.open(resource.file_url, "_blank");
    }
  };

  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      const matchesSearch = resource.title.includes(searchQuery) || 
                            resource.description?.includes(searchQuery);
      const matchesCategory = !selectedCategory || resource.category_id === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [resources, searchQuery, selectedCategory]);

  const getResourcesByCategory = (categoryId: string) => {
    return filteredResources.filter((r) => r.category_id === categoryId);
  };

  const displayName = profile?.full_name || user?.email?.split("@")[0] || "משתמש";

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="section-padding min-h-screen bg-gradient-to-b from-background to-secondary/30">
        <div className="container mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                שלום, {displayName}! 👋
              </h1>
              <p className="text-muted-foreground text-lg">
                ברוכים הבאים לספריית החומרים המקצועית שלנו
              </p>
            </div>
            <Button variant="outline" onClick={handleLogout} className="gap-2">
              <LogOut className="w-4 h-4" />
              התנתק
            </Button>
          </div>

          {/* Search & Filter with Animation */}
          <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50 mb-10">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="חפש חומרים..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pr-10 h-12 rounded-xl"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant={selectedCategory === null ? "default" : "outline"}
                    onClick={() => setSelectedCategory(null)}
                    className="rounded-xl"
                  >
                    <Filter className="w-4 h-4 ml-2" />
                    הכל
                  </Button>
                </motion.div>
                {categories.map((cat) => {
                  const Icon = iconMap[cat.icon] || FileText;
                  return (
                    <motion.div key={cat.id} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant={selectedCategory === cat.id ? "default" : "outline"}
                        onClick={() => setSelectedCategory(cat.id)}
                        className="rounded-xl"
                      >
                        <Icon className="w-4 h-4 ml-2" />
                        {cat.name_he}
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Category Cards with Animation */}
          <motion.div layout className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <AnimatePresence>
              {categories.map((category) => {
                const Icon = iconMap[category.icon] || FileText;
                const categoryResources = getResourcesByCategory(category.id);
                const isVideoCategory = category.name === "videos";
                
                return (
                  <motion.div
                    key={category.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className={`relative group bg-card rounded-2xl p-6 shadow-card border border-border/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer ${
                      selectedCategory === category.id ? "ring-2 ring-accent" : ""
                    }`}
                    onClick={() => setSelectedCategory(
                      selectedCategory === category.id ? null : category.id
                    )}
                  >
                    {/* Tooltip */}
                    <div className="tooltip-download">לחץ לצפייה בחומרים</div>
                    
                    <div className="icon-container mb-4">
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                      {category.name_he}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      {categoryResources.length} פריטים
                      {isVideoCategory && " (בקרוב)"}
                    </p>
                    {!isVideoCategory && categoryResources.length > 0 && (
                      <div className="text-sm text-accent font-medium">
                        לחץ לצפייה →
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>

          {/* Resources List with Animated Filtering */}
          <AnimatePresence mode="popLayout">
            {(selectedCategory || searchQuery) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-card rounded-2xl p-6 shadow-card border border-border/50 mb-10"
              >
                <h2 className="font-heading text-xl font-bold text-foreground mb-6">
                  {selectedCategory 
                    ? categories.find(c => c.id === selectedCategory)?.name_he 
                    : `תוצאות חיפוש: "${searchQuery}"`}
                </h2>
                
                {filteredResources.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    לא נמצאו תוצאות
                  </p>
                ) : (
                  <motion.div layout className="space-y-4">
                    <AnimatePresence>
                      {filteredResources.map((resource, index) => {
                        const fileType = fileTypeLabels[resource.file_type] || fileTypeLabels.link;
                        const isVideo = resource.file_type === "video";
                        const isNew = isNewResource(resource.created_at);
                        
                        return (
                          <motion.div
                            key={resource.id}
                            layout
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            className="relative group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                          >
                            {/* Tooltip on hover */}
                            <div className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-primary text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                              {isVideo ? "לחץ לצפייה" : "לחץ להורדה"}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-primary" />
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h4 className="font-semibold text-foreground">
                                  {resource.title}
                                </h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${fileType.color}`}>
                                  {fileType.label}
                                </span>
                                {isNew && (
                                  <motion.span 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="text-xs px-2 py-0.5 rounded-full font-bold bg-accent text-accent-foreground flex items-center gap-1"
                                  >
                                    <Sparkles className="w-3 h-3" />
                                    חדש
                                  </motion.span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {resource.description}
                              </p>
                            </div>
                            <Button
                              variant={isVideo ? "default" : "outline"}
                              size="sm"
                              className="rounded-lg gap-2 shrink-0"
                              disabled={resource.file_url === "#"}
                              onClick={() => handleResourceClick(resource)}
                            >
                              {isVideo ? (
                                <>
                                  <Play className="w-4 h-4" />
                                  צפה
                                </>
                              ) : resource.file_url === "#" ? (
                                <>
                                  <Download className="w-4 h-4" />
                                  בקרוב
                                </>
                              ) : (
                                <>
                                  <Download className="w-4 h-4" />
                                  הורד
                                </>
                              )}
                            </Button>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* CTA Banner */}
          <div className="bg-gradient-to-br from-primary to-primary-light rounded-2xl p-8 text-center">
            <h3 className="font-heading text-2xl font-bold text-primary-foreground mb-3">
              לתכנים מתקדמים יותר ולחומרים מותאמים אישית
            </h3>
            <p className="text-primary-foreground/80 mb-6 max-w-xl mx-auto">
              קבע שיחת ייעוץ עם אדם בטיחות ונבנה יחד תוכנית בטיחות מותאמת לארגון שלך
            </p>
            <Link to="/contact">
              <Button variant="hero" className="mx-auto">
                <Phone className="w-5 h-5" />
                קבע שיחת ייעוץ
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
